import React from 'react';

// Class strip, in school order. Early years sit before Class 1.
// Shared by Student Lists and the calendar pages (Scans, Sort Papers, Rubrics).
export const GRADE_BUTTONS = ['PG', 'N', 'Pr', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

// "10A+10B - BIO" → [{ grade:'10', label:'10A', letter:'A' }, { grade:'10', label:'10B', letter:'B' }]
// The " - BIO" tail is a subject stuck on the class name, so it is not a section.
export function classSectionTokens(className) {
  const head = String(className || '').split(/\s+-\s+/)[0].trim();
  if (!head) return [];
  return head.split('+').map(part => part.trim()).filter(Boolean).map(part => {
    if (/^pg$/i.test(part)) return { grade: 'PG', label: 'PG', letter: null };
    if (/^n$/i.test(part)) return { grade: 'N', label: 'N', letter: null };
    if (/^pr$/i.test(part)) return { grade: 'Pr', label: 'Pr', letter: null };
    const m = part.match(/^(\d+)\s*([A-Za-z]+)$/);
    if (!m) return null;
    const letter = m[2].toUpperCase();
    return { grade: m[1], label: `${m[1]}${letter}`, letter };
  }).filter(Boolean);
}

export function itemsInClass(items, gradeFilter, sectionFilter) {
  return (items || []).filter(item => {
    const tokens = classSectionTokens(item.class);
    if (!tokens.some(t => t.grade === gradeFilter)) return false;
    if (sectionFilter && !tokens.some(t => t.label === sectionFilter)) return false;
    return true;
  });
}

// Paper and Test are the first word of the assignment name. TEST matches
// Test. Everything else, including a blank name, is Others.
const ASSIGNMENT_WORDS = [
  { key: 'paper', label: 'Paper' },
  { key: 'test', label: 'Test' },
  { key: 'others', label: 'Others' }
];

export function assignmentBucket(name) {
  const word = String(name || '').trim().split(/\s+/)[0].toLowerCase();
  if (word === 'paper' || word === 'test') return word;
  return 'others';
}

export function itemMatchesAssignmentWord(item, wordKey) {
  if (!wordKey) return true;
  return assignmentBucket(item && item.assignment_name) === String(wordKey).toLowerCase();
}

// Class search: grade, then an optional section, then an optional subject.
// A missing grade matches everything, so calendar search can ignore this.
export function itemMatchesClass(item, gradeFilter, sectionFilter, subjectFilter) {
  if (!gradeFilter) return true;
  const tokens = classSectionTokens(item && item.class);
  if (!tokens.some(t => t.grade === gradeFilter)) return false;
  if (sectionFilter && !tokens.some(t => t.label === sectionFilter)) return false;
  if (subjectFilter && String((item && item.subject) || '').trim() !== subjectFilter) return false;
  return true;
}

function filterPillStyle(active, enabled, inbox = false) {
  return {
    minWidth: inbox ? 36 : 44,
    padding: inbox ? '5px 10px' : '8px 16px',
    borderRadius: inbox ? 8 : 10,
    border: active ? 'none' : '1px solid var(--border-color, #d1d5db)',
    background: active
      ? 'linear-gradient(135deg, #2563eb, #1d4ed8)'
      : 'var(--card-bg, white)',
    color: active ? 'white' : 'var(--text-primary, #111827)',
    fontWeight: inbox ? 600 : 700,
    fontSize: inbox ? '0.82rem' : '1.05rem',
    lineHeight: 1.2,
    cursor: enabled || active ? 'pointer' : 'not-allowed',
    opacity: enabled || active ? 1 : (inbox ? 0.45 : 0.38),
    boxShadow: active ? '0 1px 2px rgba(37,99,235,0.28)' : 'none'
  };
}

export function ClassSectionFilters({ items, gradeFilter, sectionFilter, subjectFilter, onGrade, onSection, itemNoun = 'lists', assignmentWord, onAssignmentWord, showCounts = false, appearance = 'default' }) {
  const gradeCounts = {};
  const sectionCounts = {};
  (items || []).forEach((item) => {
    const gradesSeen = new Set();
    const sectionsSeen = new Set();
    classSectionTokens(item.class).forEach((token) => {
      if (!gradesSeen.has(token.grade)) {
        gradesSeen.add(token.grade);
        gradeCounts[token.grade] = (gradeCounts[token.grade] || 0) + 1;
      }
      if (gradeFilter && token.grade === gradeFilter && token.label && !sectionsSeen.has(token.label)) {
        sectionsSeen.add(token.label);
        sectionCounts[token.label] = (sectionCounts[token.label] || 0) + 1;
      }
    });
  });
  const inbox = appearance === 'inbox';
  const pill = (active, enabled) => filterPillStyle(active, enabled, inbox);
  const countLabel = (name, count) => (showCounts ? `${name} (${count || 0})` : name);
  const gradesWithItems = new Set(
    (items || []).flatMap(item => classSectionTokens(item.class).map(t => t.grade))
  );
  const sectionButtons = gradeFilter
    ? [...new Set(
        (items || []).flatMap(item => classSectionTokens(item.class)
          .filter(t => t.grade === gradeFilter && t.letter)
          .map(t => t.label))
      )].sort((a, b) => {
        const letter = (label) => label.replace(/^\d+/, '');
        return letter(a).localeCompare(letter(b));
      })
    : [];
  const wordSource = (items || []).filter((item) => itemMatchesClass(item, gradeFilter, sectionFilter, subjectFilter));
  const wordCounts = { paper: 0, test: 0, others: 0 };
  if (onAssignmentWord) {
    wordSource.forEach((item) => { wordCounts[assignmentBucket(item.assignment_name)] += 1; });
  }
  const block = inbox
    ? {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      flexWrap: 'wrap',
      padding: '10px 14px',
      borderTop: '1px solid var(--border-color, #e5e7eb)'
    }
    : {
      background: 'var(--card-bg, white)',
      border: '1px solid var(--border-color, #e0e0e0)',
      borderRadius: 12,
      padding: '14px 16px',
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      flexWrap: 'wrap'
    };
  const caption = {
    fontSize: inbox ? '0.68rem' : '0.95rem',
    fontWeight: 700,
    letterSpacing: inbox ? 0.8 : 0.4,
    textTransform: 'uppercase',
    color: 'var(--text-secondary, #6b7280)',
    width: inbox ? 88 : 92,
    flexShrink: 0
  };
  const hint = { fontSize: inbox ? '0.82rem' : '1.05rem', color: 'var(--text-secondary, #6b7280)' };
  return (
    <div style={inbox
      ? {
        background: 'var(--card-bg, white)',
        border: '1px solid var(--border-color, #e5e7eb)',
        borderRadius: 12,
        marginBottom: 14,
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
        overflow: 'hidden'
      }
      : { display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 12, marginBottom: 18, alignItems: 'center' }}>
      <div style={inbox ? { ...block, borderTop: 'none' } : { ...block, flex: '1 1 auto' }}>
        <span style={caption}>Class</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {GRADE_BUTTONS.map(grade => {
            const active = gradeFilter === grade;
            const enabled = gradesWithItems.has(grade);
            return (
              <button
                key={grade}
                type="button"
                disabled={!enabled && !active}
                onClick={() => onGrade(grade)}
                title={enabled ? `Show ${grade} ${itemNoun}` : `No ${itemNoun} for ${grade}`}
                style={pill(active, enabled)}
              >
                {countLabel(grade, gradeCounts[grade])}
              </button>
            );
          })}
        </div>
      </div>
      {(() => {
        const assignment = onAssignmentWord && (
          <div style={inbox
            ? { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginLeft: 'auto' }
            : { ...block, flex: '0 0 auto' }}>
            <span style={{ ...caption, width: 'auto' }}>Assignment</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {ASSIGNMENT_WORDS.map((word) => {
                const active = assignmentWord === word.key;
                const enabled = wordCounts[word.key] > 0;
                const title = active
                  ? 'Show every assignment'
                  : word.key === 'others'
                    ? `Show ${itemNoun} whose assignment is not Paper or Test`
                    : enabled
                      ? `Show ${word.label} assignments`
                      : `No ${word.label} assignments`;
                return (
                  <button
                    key={word.key}
                    type="button"
                    disabled={!enabled && !active}
                    onClick={() => onAssignmentWord(active ? null : word.key)}
                    title={title}
                    style={pill(active, enabled)}
                  >
                    {word.label} ({wordCounts[word.key]})
                  </button>
                );
              })}
            </div>
          </div>
        );
        const sections = (
          <>
            <span style={caption}>Section</span>
            {!gradeFilter ? (
              <span style={hint}>
                Choose a class to see its sections.
              </span>
            ) : sectionButtons.length === 0 ? (
              <span style={hint}>
                No section letters for {gradeFilter}.
              </span>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {sectionButtons.map(label => {
                  const active = sectionFilter === label;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => onSection(label)}
                      title={active ? `Show every section of ${gradeFilter}` : `Show ${label} ${itemNoun}`}
                      style={pill(active, true)}
                    >
                      {countLabel(label, sectionCounts[label])}
                    </button>
                  );
                })}
              </div>
            )}
          </>
        );
        if (!inbox) {
          return (
            <>
              <div style={{ ...block, flex: '0 0 auto' }}>{sections}</div>
              {assignment}
            </>
          );
        }
        return (
          <div style={{ ...block, justifyContent: 'flex-start' }}>
            {sections}
            {assignment}
          </div>
        );
      })()}
    </div>
  );
}

function compareSubjectNames(a, b) {
  const aCap = /^[A-Z]/.test(a);
  const bCap = /^[A-Z]/.test(b);
  if (aCap !== bCap) return aCap ? -1 : 1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

export function SubjectColumn({ items, gradeFilter, sectionFilter, subjectFilter, onSubject, showCounts = false, appearance = 'default' }) {
  const subjectCounts = {};
  if (gradeFilter) {
    itemsInClass(items, gradeFilter, sectionFilter).forEach((item) => {
      const subject = String(item.subject || '').trim();
      if (!subject) return;
      subjectCounts[subject] = (subjectCounts[subject] || 0) + 1;
    });
  }
  const subjectButtons = gradeFilter
    ? [...new Set(
        itemsInClass(items, gradeFilter, sectionFilter)
          .map(item => (item.subject || '').trim())
          .filter(Boolean)
      )].sort(compareSubjectNames)
    : [];
  const inbox = appearance === 'inbox';
  return (
    <div style={{
      width: inbox ? 200 : 220,
      flexShrink: 0,
      alignSelf: 'flex-start',
      position: 'sticky',
      top: 12,
      maxHeight: 'calc(100vh - 80px)',
      overflowY: 'auto',
      background: 'var(--card-bg, white)',
      border: '1px solid var(--border-color, #e5e7eb)',
      borderRadius: 12,
      padding: inbox ? '10px 12px 12px' : '14px 16px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: inbox ? 6 : 10,
      boxShadow: inbox ? '0 1px 2px rgba(15,23,42,0.04)' : 'none'
    }}>
      <span style={{
        fontSize: inbox ? '0.68rem' : '0.95rem',
        fontWeight: 700,
        letterSpacing: inbox ? 0.8 : 0.4,
        textTransform: 'uppercase',
        color: 'var(--text-secondary, #6b7280)',
        padding: inbox ? '4px 4px 6px' : 0
      }}>Subject</span>
      {!gradeFilter ? (
        <span style={{ fontSize: inbox ? '0.82rem' : '1.05rem', color: 'var(--text-secondary, #6b7280)', padding: inbox ? '0 4px' : 0 }}>
          Choose a class to see its subjects.
        </span>
      ) : subjectButtons.length === 0 ? (
        <span style={{ fontSize: inbox ? '0.82rem' : '1.05rem', color: 'var(--text-secondary, #6b7280)', padding: inbox ? '0 4px' : 0 }}>
          No subjects for {sectionFilter || gradeFilter}.
        </span>
      ) : (
        subjectButtons.map(subject => {
          const active = subjectFilter === subject;
          return (
            <button
              key={subject}
              type="button"
              onClick={() => onSubject(subject)}
              title={active ? 'Show every subject' : `Show ${subject}`}
              style={{
                ...filterPillStyle(active, true, inbox),
                width: '100%',
                textAlign: 'left',
                display: inbox ? 'flex' : undefined,
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8
              }}
            >
              {inbox && showCounts ? (
                <>
                  <span>{subject}</span>
                  <span style={{
                    minWidth: 22,
                    height: 20,
                    padding: '0 6px',
                    borderRadius: 999,
                    background: active ? 'rgba(255,255,255,0.22)' : 'rgba(37,99,235,0.1)',
                    color: active ? 'white' : '#1d4ed8',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  >{subjectCounts[subject] || 0}</span>
                </>
              ) : (showCounts ? `${subject} (${subjectCounts[subject] || 0})` : subject)}
            </button>
          );
        })
      )}
    </div>
  );
}
